import BigNumber from "bignumber.js";
import type {
  BridgeQuoteFn,
  BridgeQuoteInput,
  BridgeQuoteResult,
  BridgeSubmitFn,
  BridgeSubmittedInfo,
} from "../types";

export interface BridgeRepositoryDeps {
  getQuote?: BridgeQuoteFn;
  submitBridge?: BridgeSubmitFn;
}

export interface BridgeRepository {
  canQuote: boolean;
  canSubmit: boolean;
  getQuote(input: BridgeQuoteInput): Promise<BridgeQuoteResult>;
  submitBridge(input: BridgeQuoteInput): Promise<BridgeSubmittedInfo>;
}

function isPositivePrice(value?: number): value is number {
  return typeof value === "number" && Number.isFinite(value) && value > 0;
}

function formatFallbackAmount(value: BigNumber): string {
  if (!value.isFinite() || value.isNaN() || value.lte(0)) return "";
  return value.decimalPlaces(6, BigNumber.ROUND_DOWN).toFixed();
}

function canUseOneToOneFallback(input: BridgeQuoteInput): boolean {
  return (
    input.fromToken.symbol.toLowerCase() === input.toToken.symbol.toLowerCase() ||
    (input.fromToken.chainId === input.toToken.chainId &&
      input.fromToken.address.toLowerCase() === input.toToken.address.toLowerCase())
  );
}

function getFallbackQuote(input: BridgeQuoteInput): BridgeQuoteResult {
  const fromPrice = input.fromToken.priceUsd;
  const toPrice = input.toToken.priceUsd;
  const sourceAmount =
    input.lastChangedBy === "to" ? input.toAmount : input.fromAmount;
  const amount = new BigNumber(sourceAmount || "0");

  if (!amount.isFinite() || amount.isNaN() || amount.lte(0)) {
    return {
      fromAmount: input.fromAmount,
      toAmount: input.toAmount,
    };
  }

  if (isPositivePrice(fromPrice) && isPositivePrice(toPrice)) {
    if (input.lastChangedBy === "to") {
      return {
        fromAmount: formatFallbackAmount(
          amount.multipliedBy(toPrice).dividedBy(fromPrice),
        ),
      };
    }

    return {
      toAmount: formatFallbackAmount(
        amount.multipliedBy(fromPrice).dividedBy(toPrice),
      ),
    };
  }

  if (!canUseOneToOneFallback(input)) {
    return {
      fromAmount: input.lastChangedBy === "to" ? "" : input.fromAmount,
      toAmount: input.lastChangedBy === "from" ? "" : input.toAmount,
    };
  }

  return input.lastChangedBy === "to"
    ? { fromAmount: input.toAmount }
    : { toAmount: input.fromAmount };
}

export function createBridgeRepository({
  getQuote,
  submitBridge,
}: BridgeRepositoryDeps): BridgeRepository {
  return {
    canQuote: Boolean(getQuote),
    canSubmit: Boolean(submitBridge),

    async getQuote(input) {
      if (!getQuote) {
        return getFallbackQuote(input);
      }
      return getQuote(input);
    },

    async submitBridge(input) {
      if (!submitBridge) {
        throw new Error("Bridge submit handler is not configured.");
      }
      return submitBridge(input);
    },
  };
}
